import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Button from '../components/common/Button';
import './SubmitQuotation.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const getCurrencySymbol = (code) => {
  const symbols = { USD: '$', EUR: '€', GBP: '£' };
  return symbols[code] || '$';
};

const SubmitQuotation = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { token, user } = useAuth();

  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const [isRevision, setIsRevision] = useState(false);
  const [previousLockedAmount, setPreviousLockedAmount] = useState(0);
  const [previousBidId, setPreviousBidId] = useState(null);
  const [contractId, setContractId] = useState(null);

  const createEmptyItem = () => ({ description: '', quantity: 1, unitPrice: 0, total: 0 });
  const [items, setItems] = useState(() => Array(4).fill().map(() => createEmptyItem()));

  const [leadTime, setLeadTime] = useState('');
  const [validityDays, setValidityDays] = useState('30');
  const [termsConditions, setTermsConditions] = useState('');
  const [notes, setNotes] = useState('');
  const [signatureName, setSignatureName] = useState('');

  const [totalAmount, setTotalAmount] = useState(0);

  useEffect(() => {
    const total = items.reduce((sum, item) => sum + (item.quantity * item.unitPrice), 0);
    setTotalAmount(total);
  }, [items]);

  const fetchJobAndPreviousQuotation = async () => {
    try {
      const jobRes = await fetch(`${API_BASE}/rfqs/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const jobData = await jobRes.json();
      if (!jobData.success) {
        setError('Job not found');
        setLoading(false);
        return;
      }
      setJob(jobData.rfq);

      const currentUserId = user?.id || user?._id;
      const bidsRes = await fetch(`${API_BASE}/bids`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const bidsData = await bidsRes.json();
      if (bidsData.success) {
        const allBidsForThisJob = bidsData.bids.filter(b => b.rfqId === id);
        const acceptedBid = allBidsForThisJob.find(b => b.status === 'accepted');
        let revisionMode = false;
        if (acceptedBid) {
          if (acceptedBid.vendorId.toString() === currentUserId.toString()) {
            revisionMode = true;
            setPreviousBidId(acceptedBid._id);
            setPreviousLockedAmount(acceptedBid.amount || 0);
          } else {
            setError('Another vendor\'s quotation has been accepted. You cannot submit a revised quotation for this job.');
            revisionMode = false;
          }
        }
        setIsRevision(revisionMode);
        if (revisionMode) {
          setItems(Array(4).fill().map(() => createEmptyItem()));
          setLeadTime('');
          setTermsConditions('');
          setNotes('');
          setSignatureName('');
          setValidityDays('30');
          const contractsRes = await fetch(`${API_BASE}/contracts`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const contractsData = await contractsRes.json();
          if (contractsData.success) {
            const contract = contractsData.contracts?.find(
              c => c.bidId === acceptedBid._id && c.status !== 'cancelled'
            );
            if (contract) setContractId(contract._id);
          }
        } else {
          setItems(Array(4).fill().map(() => createEmptyItem()));
          setPreviousLockedAmount(0);
          setPreviousBidId(null);
          setContractId(null);
        }
      }
    } catch (err) {
      console.error('Fetch error:', err);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchJobAndPreviousQuotation();
  }, [id]);

  const addItem = () => setItems([...items, createEmptyItem()]);

  const removeItem = (index) => {
    if (items.length <= 1) return;
    setItems(items.filter((_, i) => i !== index));
  };

  const updateItem = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;
    if (field === 'quantity' || field === 'unitPrice') {
      newItems[index].total = (newItems[index].quantity || 0) * (newItems[index].unitPrice || 0);
    }
    setItems(newItems);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validItems = items.filter(
      item => item.description && item.description.trim() !== '' && item.quantity > 0 && item.unitPrice > 0
    );
    if (validItems.length === 0) {
      alert('Please add at least one valid quotation item with description, quantity > 0, and price > 0');
      return;
    }
    if (!leadTime) {
      alert('Please enter lead time');
      return;
    }
    if (!signatureName.trim()) {
      alert('Please enter your authorized signatory name');
      return;
    }
    setSubmitting(true);

    const totalNewItems = validItems.reduce((sum, item) => sum + (item.quantity * item.unitPrice), 0);

    const payload = {
      rfqId: id,
      originalAmount: Math.round(totalNewItems * 100) / 100,
      amount: Math.round(totalNewItems * 100) / 100,
      commission: 0,
      commissionAmount: 0,
      currency: job?.currency || 'USD',
      leadTime: leadTime + ' days',
      technicalProposal: notes || 'No additional comments',
      quotationItems: validItems.map(item => ({
        description: item.description.trim(),
        quantity: parseInt(item.quantity),
        unitPrice: parseFloat(item.unitPrice),
        total: parseFloat(item.quantity) * parseFloat(item.unitPrice)
      })),
      validityDays: parseInt(validityDays),
      termsConditions: termsConditions || '',
      signatureName: signatureName.trim(),
      isRevision: isRevision,
      previousQuotationId: previousBidId || null,
      previousLockedAmount: previousLockedAmount,
      contractId: contractId || null
    };

    try {
      const res = await fetch(`${API_BASE}/bids`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (data.success) {
        alert(isRevision ? '✅ Revised quotation submitted!' : '✅ Quotation submitted successfully!');
        navigate('/service-provider/my-offers');
      } else {
        setError(data.message || 'Submission failed');
      }
    } catch (err) {
      console.error('Submit error:', err);
      setError('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const getBudgetDisplay = () => {
    if (!job) return 'N/A';
    const currency = job.currency || 'USD';
    const symbol = getCurrencySymbol(currency);
    if (job.budgetType === 'quotation' || (!job.budget && !job.budgetMin)) return 'Request for Quotation';
    if (job.budgetType === 'hourly') return `${symbol}${(job.budget || job.budgetMin || 0).toLocaleString()}/hr`;
    if (job.budgetMin && job.budgetMax) {
      return `${symbol}${job.budgetMin.toLocaleString()} – ${symbol}${job.budgetMax.toLocaleString()}`;
    }
    return `${symbol}${(job.budget || job.budgetMin || 0).toLocaleString()}`;
  };

  if (loading) return <div className="loading-spinner">Loading...</div>;
  if (!job) return <div className="error-message">Job not found</div>;

  const currency = job.currency || 'USD';
  const currencySymbol = getCurrencySymbol(currency);
  const grandTotal = previousLockedAmount + totalAmount;

  return (
    <div className="submit-quotation-page">
      <button className="back-btn" onClick={() => navigate(-1)}>
        <i className="fas fa-arrow-left"></i> Back
      </button>

      <div className="page-header">
        <h1>
          <i className={`fas ${isRevision ? 'fa-edit' : 'fa-file-invoice'}`}></i>
          {isRevision ? 'Revise Quotation' : 'Submit Quotation'}
        </h1>
        <p>
          {isRevision
            ? 'Add new items to revise your previously accepted quotation.'
            : 'Create a detailed quotation with item-wise breakdown. Minimum 1 valid item required.'}
        </p>
      </div>

      {error && (
        <div className="error-banner">
          <i className="fas fa-exclamation-circle"></i> {error}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="quotation-card">
          <h3><i className="fas fa-info-circle"></i> Job Details</h3>
          <div className="job-info-grid">
            <div className="job-info-item">
              <span className="label">Vessel</span>
              <span className="value">{job.vesselName}</span>
            </div>
            <div className="job-info-item">
              <span className="label">Service Type</span>
              <span className="value">{job.serviceType}</span>
            </div>
            <div className="job-info-item">
              <span className="label">Current Port</span>
              <span className="value">{job.port}</span>
            </div>
            <div className="job-info-item">
              <span className="label">Budget</span>
              <span className="value highlight">{getBudgetDisplay()}</span>
            </div>
            <div className="job-info-item">
              <span className="label">Currency</span>
              <span className="value">{currency}</span>
            </div>
            <div className="job-info-item">
              <span className="label">Deadline</span>
              <span className="value">{new Date(job.deadline).toLocaleDateString()}</span>
            </div>
            {isRevision && (
              <div className="job-info-item">
                <span className="label">Mode</span>
                <span className="value" style={{ color: '#f59e0b', fontWeight: '700' }}>
                  📝 Revision Mode
                </span>
              </div>
            )}
          </div>
        </div>

        {isRevision && previousLockedAmount > 0 && (
          <div className="quotation-card locked-card">
            <h3><i className="fas fa-lock"></i> Previously Accepted Quotation</h3>
            <div className="locked-info">
              <div className="locked-row">
                <span>💰 Previous Contract Value:</span>
                <strong>{currencySymbol}{previousLockedAmount.toLocaleString()} {currency}</strong>
              </div>
              <div className="locked-note">
                <i className="fas fa-info-circle"></i> Add new items below to extend the contract.
              </div>
            </div>
          </div>
        )}

        <div className="quotation-card">
          <div className="items-header">
            <h3>
              <i className="fas fa-list-ul"></i>
              {isRevision ? 'Additional Items (New)' : 'Quotation Items'}
            </h3>
            <Button type="button" variant="outline" size="small" onClick={addItem} icon="fa-plus">
              Add Item
            </Button>
          </div>

          <div className="items-table">
            <div className="items-table-header">
              <span>Description</span>
              <span>Quantity</span>
              <span>Unit Price ({currency})</span>
              <span className="col-total">Total ({currency})</span>
              <span className="col-action"></span>
            </div>

            {items.map((item, index) => (
              <div key={index} className="item-row">
                <div className="col-desc">
                  <input
                    type="text"
                    value={item.description}
                    onChange={(e) => updateItem(index, 'description', e.target.value)}
                    placeholder="Enter item description..."
                  />
                </div>
                <div className="col-qty">
                  <input
                    type="number"
                    value={item.quantity}
                    onChange={(e) => updateItem(index, 'quantity', parseInt(e.target.value) || 0)}
                    min="1"
                  />
                </div>
                <div className="col-price">
                  <input
                    type="number"
                    value={item.unitPrice}
                    onChange={(e) => updateItem(index, 'unitPrice', parseFloat(e.target.value) || 0)}
                    min="0"
                    step="0.01"
                  />
                </div>
                <div className="col-total">
                  <span className="item-total">
                    {currencySymbol}{(item.quantity * item.unitPrice).toLocaleString()}
                  </span>
                </div>
                <div className="col-action">
                  <button
                    type="button"
                    className="remove-item-btn"
                    onClick={() => removeItem(index)}
                    disabled={items.length <= 1}
                  >
                    <i className="fas fa-trash-alt"></i>
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="totals-section">
            <div className="section-title">
              <i className="fas fa-calculator"></i> Cost Breakdown ({currency})
            </div>
            {isRevision ? (
              <>
                <div className="total-row">
                  <span>Previous Contract:</span>
                  <strong>{currencySymbol}{previousLockedAmount.toLocaleString()}</strong>
                </div>
                <div className="total-row">
                  <span>New Items Subtotal:</span>
                  <strong>{currencySymbol}{totalAmount.toLocaleString()}</strong>
                </div>
                <div className="total-row you-receive">
                  <span>✅ New Contract Total:</span>
                  <strong>{currencySymbol}{grandTotal.toLocaleString()}</strong>
                </div>
              </>
            ) : (
              <>
                <div className="total-row">
                  <span>Total Quotation:</span>
                  <strong>{currencySymbol}{totalAmount.toLocaleString()}</strong>
                </div>
                <div className="total-row you-receive">
                  <span>✅ You Will Receive:</span>
                  <strong>{currencySymbol}{totalAmount.toLocaleString()}</strong>
                </div>
              </>
            )}
          </div>
        </div>

        <div className="quotation-card">
          <h3><i className="fas fa-cog"></i> Quotation Details</h3>
          <div className="form-row">
            <div className="form-group">
              <label>Lead Time (Days) *</label>
              <input
                type="number"
                value={leadTime}
                onChange={(e) => setLeadTime(e.target.value)}
                min="1"
                required
                placeholder="e.g., 5"
              />
            </div>
            <div className="form-group">
              <label>Validity Period</label>
              <select value={validityDays} onChange={(e) => setValidityDays(e.target.value)}>
                <option value="15">15 Days</option>
                <option value="30">30 Days</option>
                <option value="45">45 Days</option>
                <option value="60">60 Days</option>
                <option value="90">90 Days</option>
              </select>
            </div>
          </div>
          <div className="form-group">
            <label>Terms & Conditions</label>
            <textarea
              rows="3"
              value={termsConditions}
              onChange={(e) => setTermsConditions(e.target.value)}
              placeholder="Enter terms and conditions..."
            />
          </div>
          <div className="form-group">
            <label>Additional Notes</label>
            <textarea
              rows="3"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any additional notes or technical details..."
            />
          </div>
        </div>

        <div className="quotation-card">
          <h3><i className="fas fa-signature"></i> Authorized Signatory</h3>
          <div className="form-group">
            <label>Full Name (as authorized signatory) *</label>
            <input
              type="text"
              value={signatureName}
              onChange={(e) => setSignatureName(e.target.value)}
              placeholder="e.g., Capt. John Smith, Managing Director"
              required
            />
          </div>
        </div>

        <div className="form-actions">
          <Button variant="secondary" onClick={() => navigate(-1)} type="button">Cancel</Button>
          <Button variant="primary" type="submit" disabled={submitting}>
            {submitting ? 'Submitting...' : (isRevision ? 'Submit Revised Quotation' : 'Submit Quotation')}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default SubmitQuotation;